/**
 * Peer presentational atoms for the agent channel tabs — the initials avatar
 * (`PeerAvatar`) and the trust-tier chip (`PeerTierBadge`), shared by the
 * conversation list rows and the thread header.
 *
 * P1 is initials-only (no real profile photos — those are LGPD-gated behind
 * D-2 in `ai-docs/lexide-agent-im-tabs-options-plan.md`). The avatar glyph is
 * the peer's initials over a tint deterministically chosen from the label, so
 * the same correspondent keeps the same color across rows and threads.
 * Decorative: the name always sits in the adjacent text, so it is `aria-hidden`.
 */
import { contactInitials } from "@/lib/peer-identity";
import { TIER_META } from "@/lib/contacts-logic";
import type { TrustTier } from "@/types/contacts";
import { cn } from "@/lib/utils";

/** Theme-aware tints (light + dark). Indexed by a stable hash of the label. */
const TINTS = [
  "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300",
  "bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300",
  "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
  "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300",
  "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300",
  "bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300",
] as const;

const NEUTRAL_TINT =
  "bg-cream-200 text-cream-500 dark:bg-coffee-800 dark:text-cream-500";

function tintFor(label: string): string {
  let h = 0;
  for (const ch of label) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return TINTS[h % TINTS.length]!;
}

export function PeerAvatar({ label, size = 28 }: { label: string; size?: number }) {
  const initials = contactInitials(label);
  return (
    <span
      aria-hidden
      className={cn(
        "inline-flex shrink-0 select-none items-center justify-center rounded-full font-semibold uppercase leading-none",
        initials ? tintFor(label) : NEUTRAL_TINT,
      )}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
    >
      {initials || "·"}
    </span>
  );
}

/**
 * The peer's trust tier as a compact chip (`TIER_META` supplies the localized
 * label — the same vocabulary the Contacts surface uses). Rendered only when a
 * peer resolved to a curated contact, so it never appears for a raw coordinate.
 */
export function PeerTierBadge({ tier }: { tier: TrustTier }) {
  return (
    <span
      data-testid="channel-peer-tier"
      className="shrink-0 rounded bg-cream-200/70 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-coffee-600 dark:bg-coffee-800 dark:text-cream-400"
    >
      {TIER_META[tier].label}
    </span>
  );
}
